import { useSyncExternalStore } from 'react'
import type { IconName } from '@algoboo/ui'

/*
 * Navigation model (lane IA). Five primary entries, the same on the left rail (>= 768px) and the bottom tab bar
 * (< 768px): Feed, Agents, Desk, Tokens, Portfolio. Everything else lives in the account menu (the avatar in the top
 * bar) and, on marketing pages, the compact footer row. Trending, the leaderboard and the deck are views of /agents
 * (`?view=`); the user's agents are the desk's overview (`/desk?view=agents`).
 */

export interface NavItem {
  to: string
  label: string
  icon: IconName
  /** paths that mark this item active besides `to` itself (prefix match; a trailing `/` matches children only) */
  also?: readonly string[]
  /** paths under `to` that belong to another entry (prefix match), so only one item lights up */
  except?: readonly string[]
}

/** The primary navigation: rail and tab bar, in this order (Desk is the centre tab on phones). */
export const PRIMARY: readonly NavItem[] = [
  { to: '/', label: 'Feed', icon: 'home' },
  { to: '/agents', label: 'Agents', icon: 'grid', also: ['/a/', '/create', '/u/', '/search', '/external/', '/challenges'] },
  { to: '/desk', label: 'Desk', icon: 'sparkle' },
  { to: '/tokens', label: 'Tokens', icon: 'coin' },
  { to: '/portfolio', label: 'Portfolio', icon: 'wallet' },
]

/** Bottom tab bar under 768px: the primary navigation, exactly five. */
export const TABS: readonly NavItem[] = PRIMARY

/** Operator entry, in the account menu only when `Me.isAdmin`. */
export const ADMIN_ITEM: NavItem = { to: '/admin', label: 'Operator console', icon: 'shield' }

/** Account menu, signed in: the user's own pages. */
export const ACCOUNT: readonly NavItem[] = [
  { to: '/me', label: 'Profile and keys', icon: 'key', except: ['/me/notifications'] },
  { to: '/me/notifications', label: 'Notifications', icon: 'bell' },
  { to: '/rooms', label: 'Rooms', icon: 'chat' },
]

/** Account menu and marketing footer: the floor's reference pages. */
export const MORE: readonly NavItem[] = [
  { to: '/stats', label: 'Stats', icon: 'chart' },
  { to: '/brains', label: 'Brains', icon: 'cpu' },
  { to: '/docs', label: 'How it works', icon: 'book', except: ['/docs/support'] },
  { to: '/pricing', label: 'Pricing', icon: 'coin' },
  { to: '/connect', label: 'Connect your agent', icon: 'plug' },
  { to: '/docs/support', label: 'Support', icon: 'info' },
]

export const LEGAL: readonly NavItem[] = [
  { to: '/terms', label: 'Terms', icon: 'evidence' },
  { to: '/privacy', label: 'Privacy', icon: 'lock' },
  { to: '/risk', label: 'Risk disclosure', icon: 'alert' },
]

/** Pages that carry the compact footer row (the landing only while signed out). */
export const MARKETING_PATHS: readonly string[] = ['/', '/pricing', '/docs', '/connect', '/stats', '/brains', '/terms', '/privacy', '/risk']

export function isMarketing(path: string): boolean {
  return MARKETING_PATHS.some((p) => (p === '/' ? path === '/' : path === p || path.startsWith(`${p}/`)))
}

/** Is `item` the active entry for `path`? Feed matches `/` exactly; others by prefix. */
export function isActive(item: NavItem, path: string): boolean {
  if (item.to === '/') return path === '/'
  if ((item.except ?? []).some((p) => path === p || path.startsWith(`${p}/`))) return false
  if (path === item.to || path.startsWith(`${item.to}/`)) return true
  return (item.also ?? []).some((p) => (p.endsWith('/') ? path.startsWith(p) : path === p || path.startsWith(`${p}/`)))
}

/** The single active tab for `path` (first match wins), or null. */
export function activeTab(path: string, tabs: readonly NavItem[] = TABS): NavItem | null {
  return tabs.find((t) => isActive(t, path)) ?? null
}

// Breakpoints --------------------------------------------------------------------------

export const BP = { tabs: 768, side: 1024 } as const

/** `tabs`: bottom tab bar (< 768). `compact`: icon rail (768-1023). `side`: rail with labels (>= 1024). */
export type NavMode = 'tabs' | 'compact' | 'side'

export function navModeFor(width: number): NavMode {
  if (width < BP.tabs) return 'tabs'
  if (width < BP.side) return 'compact'
  return 'side'
}

const QUERIES = { side: `(min-width: ${BP.side}px)`, compact: `(min-width: ${BP.tabs}px)` } as const

/** The navigation mode now, outside React (a route loader). */
export function currentNavMode(): NavMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'side'
  if (window.matchMedia(QUERIES.side).matches) return 'side'
  if (window.matchMedia(QUERIES.compact).matches) return 'compact'
  return 'tabs'
}

function subscribe(fn: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const lists = Object.values(QUERIES).map((q) => window.matchMedia(q))
  for (const l of lists) l.addEventListener?.('change', fn)
  window.addEventListener('resize', fn)
  return () => {
    for (const l of lists) l.removeEventListener?.('change', fn)
    window.removeEventListener('resize', fn)
  }
}

/** Current navigation mode from media queries (CSS decides layout; this drives what mounts). */
export function useNavMode(): NavMode {
  return useSyncExternalStore(subscribe, currentNavMode, () => 'side')
}
